<script setup lang="ts">
import type { ContentPackage } from '~/models/Content/ContentPackage'
import { useI18n } from 'vue-i18n'
import { computed, onBeforeUnmount, onMounted, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuery } from '@tanstack/vue-query'
import { faArrowLeft } from '@fortawesome/duotone-regular-svg-icons'
import { firstOf } from '~/utils/queryUtils'
import { setTitle } from '~/utils/dom'
import useAuthStore from '~/stores/auth'
import { ColorTheme } from '~/models/Content/Color'
import useTeacherGuide from '~/composables/useTeacherGuide'
import useSlugify from '~/composables/useSlugify'
import { useProductQuery } from '~/composables/useProductQuery'
import { useMedia } from '~/composables/useMedia'
import { useContentHelper } from '~/composables/useContentHelper'
import { useColorTheme } from '~/composables/useColorTheme'
import useCardStyles from '~/composables/useCardStyles'
import { useAppColor } from '~/composables/useAppColor'
import useContentApi from '~/api/contentApi'
import RichTextRenderer from '~/components/utils/RichTextRenderer.vue'
import HeaderViewBannerGrid from '~/components/utils/HeaderViewBannerGrid.vue'
import CopyrightButton from '~/components/utils/CopyrightButton.vue'
import Button from '~/components/ui/Button.vue'
import AuSkeletonWrapper from '~/components/ui/AuSkeletonWrapper.vue'
import AuSkeleton from '~/components/ui/AuSkeleton.vue'
import Header8To10Section from '~/components/subject/gradetypes/Header8To10Section.vue'
import Image from '~/components/media/Image.vue'
import SimpleCard from '~/components/cards/SimpleCard.vue'

const { productId, locationId } = defineProps<{
  productId: number
  locationId: number
}>()

const { t } = useI18n()
const { packageTypes, isContentHeader, isHeader, isTeacherArticle } = useContentHelper()
const { findContents } = useContentApi()
const { slugify } = useSlugify()
const { isTeacher, isAuthenticated } = storeToRefs(useAuthStore())
const { set: setAppColor, clear: clearAppColor } = useAppColor()

const { data: header, isLoading: isLoadingHeader } = useQuery({
  enabled: computed(() => !!locationId),
  queryKey: computed(() => ['header-view', locationId]),
  queryFn: () => findContents<ContentPackage>({
    locationIdCriterion: [locationId],
    contentTypeCriterion: packageTypes,
  }, 1).then(([data]) => data),
  staleTime: Infinity,
})

const { data: children, isLoading: isLoadingChildren } = useQuery({
  enabled: computed(() => !!header?.value && isAuthenticated.value),
  queryKey: computed(() => ['header-children', locationId]),
  queryFn: () => findContents({
    parentLocationIdCriterion: [locationId],
    sortField: header!.value!.sortField,
    sortOrder: header!.value!.sortOrder,
  }),
  staleTime: Infinity,
})

const { data: product, isLoading: isLoadingProduct } = useProductQuery(productId)

const { teacherArticle } = useTeacherGuide({
  contentId: computed(() => Number(header.value?.teacherGuide?.destinationContentId)),
  enabled: computed(() => isAuthenticated.value && !!header?.value?.teacherGuide?.destinationContentId),
  withContent: false,
})

const sections = computed(() => (children?.value ?? []).filter(item => isHeader(item)))
const other = computed(() => (children?.value ?? []).filter(item => !isHeader(item) && !isTeacherArticle(item)))
const isLoading = computed(() => isLoadingHeader.value || isLoadingChildren.value || isLoadingProduct.value)
const colorTheme = computed(() => product?.value?.colorTheme ?? header?.value?.colorTheme ?? ColorTheme.VioletYellow)

const { theme } = useColorTheme(colorTheme)
const { cardStyle, themeStyle } = useCardStyles(theme)

const mediaContentId = computed(() => header.value?.image && Number(header.value.image.destinationContentId))
const { data: bgMedia, isLoading: bgMediaIsLoading } = firstOf(useMedia(computed(() => mediaContentId.value ? [mediaContentId.value] : [])))

onMounted(() => {
  if (theme.value) {
    setAppColor({
      themeColor: theme.value[60],
      bgColor: theme.value[10]
    })
  }
})

onBeforeUnmount(() => clearAppColor())

watch([header, product], () => {
  if (header.value)
    setTitle(header.value.title)
  setAppColor({
    themeColor: theme.value[60],
    bgColor: theme.value[10]
  })
}, { immediate: true })
</script>

<template>
  <div v-if="isLoading">
    <AuSkeletonWrapper>
      <HeaderViewBannerGrid>
        <div class="grid-in-header-info py-24 md:py-32">
          <AuSkeleton height="4rem" />
          <AuSkeleton
            height="1.5rem"
            width="80%"
          />
          <AuSkeleton
            height="1.5rem"
            width="70%"
          />
          <AuSkeleton
            height="1.5rem"
            width="80%"
          />
          <AuSkeleton
            height="1.5rem"
            width="65%"
          />
          <AuSkeleton
            height="1.5rem"
            width="70%"
          />
        </div>
      </HeaderViewBannerGrid>
    </AuSkeletonWrapper>

    <section
      v-for="index in 2"
      :key="index"
    >
      <AuSkeletonWrapper class="max-w-screen-au mx-auto space-y-4 px-4 py-8 sm:px-8">
        <AuSkeleton
          height="36px"
          width="120px"
        />
        <ul class="xs:grid-cols-2 grid grid-cols-1 gap-8 md:grid-cols-3 lg:grid-cols-4">
          <AuSkeleton
            v-for="i in 4"
            :key="i"
            height="280px"
          />
        </ul>
      </AuSkeletonWrapper>
    </section>
  </div>

  <div
    v-else
    class="grid gap-20 text-(--theme-60)"
    :style="themeStyle"
  >
    <HeaderViewBannerGrid
      v-if="header"
      class="bg-(--theme-60) text-(--theme-10)"
    >
      <Teleport
        v-if="product && isAuthenticated"
        to="#header-navigation"
      >
        <Button
          :to="{
            name: 'product',
            params: { locationId: product.locationId, slug: slugify(product.title) },
          }"
          :color="theme[50]"
          size="xl"
          variant="outline"
          shade="light"
        >
          <font-awesome-icon :icon="faArrowLeft" />
          {{ product.title }}
        </Button>
      </Teleport>
      <div class="grid-in-header-info flex flex-col items-start gap-4 self-center py-24 md:py-32">
        <h1
          class="mb-4 text-4xl font-black"
          v-text="header.title"
        />
        <div
          v-if="header.intro"
          class="prose prose-a:text-[currentColor] mb-4 text-(--theme-10)"
        >
          <RichTextRenderer :text="header.intro" />
        </div>
        <Button
          v-if="isTeacher && teacherArticle"
          :to="{ name: 'teacher_article', params: { locationId: teacherArticle.locationId, slug: slugify(teacherArticle.title) } }"
          color="teacher"
        >
          {{ t('filters.forTeacher') }}
        </Button>
      </div>
      <Image
        v-if="mediaContentId"
        :content="mediaContentId"
        class="grid-in-image md:grid-in-image-top-full aspect-video !h-auto min-h-full w-full max-w-full place-self-end object-cover object-left md:h-0 md:[clip-path:polygon(20%_0%,100%_0%,100%_100%,0%_100%)]"
        :class="!bgMediaIsLoading && 'animate-fade'"
        width="700px"
      />
      <CopyrightButton
        v-if="bgMedia && 'copyright' in bgMedia"
        :copyright="bgMedia.copyright"
        class="col-end-[image] row-start-2 m-4 place-self-end"
      />
    </HeaderViewBannerGrid>

    <section
      v-for="(section, index) in sections"
      :key="section.contentId"
      class="grid grid-cols-1 place-items-center last:pb-20"
      :class="{ 'bg-(--theme-20) py-20 [.py-20+&]:-mt-20 [.py-20+&]:pt-0': isContentHeader(section) && section.viewMode === 'task' }"
      :style="isContentHeader(section) && cardStyle(section)"
    >
      <div v-if="header" class="max-w-screen-au flex w-full flex-col gap-24 px-4 sm:px-8">
        <Header8To10Section
          :header="header"
          :section="section"
          :index="index"
          :grade-filter="false"
        />
      </div>
    </section>

    <section
      v-if="other && other.length > 0"
      class="max-w-screen-au mx-auto flex w-full flex-col gap-4 px-4 pb-20 sm:px-8"
    >
      <h2
        v-if="sections.length > 0"
        class="font-tiempos text-4xl text-(--theme-50)"
        v-text="t('labels.other')"
      />
      <ul class="xs:grid-cols-2 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4">
        <li
          v-for="content in other"
          :key="content.locationId"
          class="flex"
        >
          <SimpleCard :resource="content" />
        </li>
      </ul>
    </section>
  </div>
</template>
